import { addDays, isSameDay } from "date-fns";
import { OUTAGE_DAY } from "./constants";

export const formatPrice = (amount: number) => {
  let roundedAmount = Math.ceil(amount);

  if (roundedAmount !== amount) {
    roundedAmount = roundedAmount - 0.01;
  }

  return roundedAmount;
};

export type TimezonePrice = {
  basePlanId: string;
  amount: number;
  currency: string;
  amountOriginal: number;
  currencyOriginal: string;
  offers: TimezonePriceOffer[];
};

export type TimezonePriceOffer = {
  offerId: string;
  reccurenceCount: number;
  duration: string; // ISO 8601 duration [P1W, P1M, P1Y, ...]
};

// find price data for a given storeId and timezonePrices. Sometimes base plans have offers (trial). We want to cut the offer part.
export const findTimezonePriceData = (storeId: string, timezonePrices: TimezonePrice[]) => {
  if (!timezonePrices || !Array.isArray(timezonePrices)) {
    console.error("Invalid timezonePrices:", timezonePrices);
    return null;
  }

  let priceData = timezonePrices?.find((t) => t.basePlanId === storeId);

  if (!priceData && storeId.split("@").length >= 2) {
    const [productId, basePlanId, offerId] = storeId.split("@");
    const modifiedStoreId = `${productId}@${basePlanId}`;
    priceData = timezonePrices?.find((t) => t.basePlanId === modifiedStoreId);
    if (priceData && offerId.startsWith("promo")) {
      const discountMatch = offerId.match(/^promo(\d+)$/);
      let multiplier = 1;
      if (discountMatch) {
        multiplier = (100 - parseInt(discountMatch[1])) / 100;
      } else {
        // 60% discount
        multiplier = 0.4;
      }
      priceData = {
        ...priceData,
        amount: priceData.amount * multiplier
      };
    }
  }

  return priceData;
};

export const isSubForMonth = (storeId: string) => {
  const lowercaseId = storeId.toLowerCase();
  return lowercaseId.includes("onemonth") || lowercaseId.includes("1month") || lowercaseId.includes("one-month");
};

export const isSubFor3months = (storeId: string) => {
  const lowercaseId = storeId.toLowerCase();
  return lowercaseId.includes("3months") || lowercaseId.includes("threemonths") || lowercaseId.includes("three-months");
};

export const isSubForYear = (storeId: string) => {
  const lowercaseId = storeId.toLowerCase();
  return (
    lowercaseId.includes("@year") ||
    lowercaseId.includes("@p1y") ||
    ["year", "yearly", "yearlyly", "yearextra", "yearly_cheap", "yearly_cheap_apple"].includes(lowercaseId)
  );
};

export const isOutageApplicable = (created: Date | number) => {
  const createdDate =
    typeof created === "number"
      ? addDays(new Date(), -created) // If number provided, calculate date X days before outage
      : created; // If Date provided, use it directly

  // Only apply outage to users created before the outage day
  if (createdDate > OUTAGE_DAY) {
    return false;
  }
  return isSameDay(new Date(), addDays(OUTAGE_DAY, 1)) || isSameDay(new Date(), OUTAGE_DAY);
};

export const isSubForFamily = (storeId: string) => storeId.toLowerCase().includes("family");

export const isSubWithTrial = (storeId: string) => storeId.toLowerCase().includes("trial");

export const removeDiacritics = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[¿,.?!¡]/g, "");
};
